import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { type ForwardedRef, type RefObject, forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import useResizeObserver from '../../hooks/useResizeObserver';
import { Icon } from '../Icon';
import HeaderRow from './HeaderRow';
import RowList from './RowList';
import TableElement from './server/elements/Table';
import styles from './server/elements/Table.module.css';
import type { TableConfig } from './types';
import { TableStoreProvider, useExpandable, useIsPivot, usePivotEntities, useResizeContext } from './utils/TableStore';
function Header() {
  const $ = _c(1);
  const pivot = useIsPivot();
  if (pivot) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <HeaderRow />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function ScrollToEndButton({
  tableRef,
  label
}: {
  tableRef: RefObject<HTMLTableElement | null>;
  label: string;
}) {
  const [isMaxScroll, setIsMaxScroll] = useState(false);
  useEffect(() => {
    function setMaxScroll(element: HTMLTableElement) {
      const maxScroll = Math.round(element.scrollLeft + element.clientWidth) >= Math.round(element.scrollWidth);
      setIsMaxScroll(maxScroll);
    }
    if (!tableRef.current) return;
    const container = tableRef.current.parentElement as HTMLTableElement;
    setMaxScroll(container);
    function listener(event: Event) {
      const target = event.target as HTMLTableElement;
      setMaxScroll(target);
    }
    container.addEventListener('scroll', listener);
    return () => {
      container.removeEventListener('scroll', listener);
    };
  }, [tableRef]);
  const onClick = useCallback(() => {
    if (!tableRef.current) return;
    const container_0 = tableRef.current.parentElement as HTMLDivElement;
    container_0.scrollTo({
      left: container_0.scrollWidth - container_0.clientWidth,
      behavior: 'smooth'
    });
  }, [tableRef]);
  return <button onClick={onClick} className={classNames(styles.scrollToEndButton, {
    [styles.hidden!]: isMaxScroll
  })} aria-hidden={isMaxScroll} title={label}>
      <div className={styles.iconWrapper}>
        <Icon name="chevronRight" color="cta-secondary" size="300" className={styles.icon} />
      </div>
    </button>;
}
const TableInner = forwardRef(function TableInner(t0, ref) {
  const $ = _c(9);
  const {
    className
  } = t0;
  const {
    resizing,
    touchEnabled
  } = useResizeContext();
  const pivot = useIsPivot();
  const expandable = useExpandable();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Header />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = touchEnabled ?? undefined;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <RowList />;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== className || $[3] !== expandable || $[4] !== pivot || $[5] !== ref || $[6] !== resizing || $[7] !== t2) {
    t4 = <TableElement ref={ref} resizing={resizing} head={t1} pivot={pivot} expandable={expandable} className={className} touchEnabled={t2}>{t3}</TableElement>;
    $[2] = className;
    $[3] = expandable;
    $[4] = pivot;
    $[5] = ref;
    $[6] = resizing;
    $[7] = t2;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
});
function Debug() {
  const $ = _c(15);
  const {
    columnWidths,
    resizing,
    touchEnabled
  } = useResizeContext();
  const entities = usePivotEntities();
  const t0 = resizing ? "1px solid red" : "1px solid black";
  let t1;
  if ($[0] !== t0) {
    t1 = {
      border: t0,
      marginBlockStart: 50,
      padding: 5
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = touchEnabled ? "touch" : "mouse";
  let t3;
  if ($[2] !== columnWidths) {
    t3 = JSON.stringify(columnWidths.map(_temp));
    $[2] = columnWidths;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = <div style={t1}>[{t2}]: {t3}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== entities) {
    t5 = JSON.stringify(entities, null, 2);
    $[8] = entities;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <pre>{t5}</pre>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t6) {
    t7 = <>{t4}{t6}</>;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
function _temp(c) {
  return c.width;
}
function Table(t0) {
  const $ = _c(25);
  const {
    columns,
    data,
    getRowId,
    pivot,
    expandable,
    id: id_,
    className,
    pivotLabelColumnConfig,
    debug,
    scrollToEndLabel
  } = t0;
  const generatedId = useId();
  const id = id_ ?? generatedId;
  let t1;
  if ($[0] !== columns || $[1] !== expandable || $[2] !== getRowId || $[3] !== id || $[4] !== pivot || $[5] !== pivotLabelColumnConfig) {
    t1 = {
      columns,
      getRowId,
      pivot,
      expandable,
      id,
      pivotLabelColumnConfig
    };
    $[0] = columns;
    $[1] = expandable;
    $[2] = getRowId;
    $[3] = id;
    $[4] = pivot;
    $[5] = pivotLabelColumnConfig;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const config = t1;
  const tableRef = useRef(null);
  const containerRef = useRef(null);
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      ref: containerRef
    };
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    width: wrapperWidth
  } = useResizeObserver(t2);
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      ref: tableRef
    };
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    width: tableWidth
  } = useResizeObserver(t3);
  const overflows = (wrapperWidth ?? 0) < (tableWidth ?? 0);
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ref => {
      tableRef.current = ref;
      containerRef.current = ref?.parentElement as HTMLDivElement;
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== className) {
    t5 = <TableInner ref={t4} className={className} />;
    $[10] = className;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== overflows || $[13] !== scrollToEndLabel) {
    t6 = overflows && <ScrollToEndButton tableRef={tableRef} label={scrollToEndLabel} />;
    $[12] = overflows;
    $[13] = scrollToEndLabel;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5 || $[16] !== t6) {
    t7 = <div className={styles.wrapper}>{t5}{t6}</div>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== debug) {
    t8 = debug && <Debug />;
    $[18] = debug;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== config || $[21] !== data || $[22] !== t7 || $[23] !== t8) {
    t9 = <TableStoreProvider data={data} config={config} tableRef={tableRef}>{t7}{t8}</TableStoreProvider>;
    $[20] = config;
    $[21] = data;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
export default Table;